import type { StatusOption } from "@/lib/types";

/** A stable id for a brand-new status option. */
export function newOptionId(): string {
  return typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `opt_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Trim every label and drop rows left blank. Only ever brand-new, unnamed rows
 * are dropped — callers guard existing options against blanking via
 * {@link hasBlankExistingLabel} first. Spreads the original so archived / role /
 * color / goal_date survive untouched (and key order is preserved, which
 * {@link optionsChanged} relies on).
 */
export function cleanOptions(draft: StatusOption[]): StatusOption[] {
  return draft.map((o) => ({ ...o, label: o.label.trim() })).filter((o) => o.label.length > 0);
}

/**
 * True if the draft blanks the label of an option that already existed. Saving
 * that would silently drop the option on clean and orphan every cell that stored
 * its id — block it and steer the user to archive instead.
 */
export function hasBlankExistingLabel(draft: StatusOption[], original: StatusOption[]): boolean {
  const originalIds = new Set(original.map((o) => o.id));
  return draft.some((o) => o.label.trim().length === 0 && originalIds.has(o.id));
}

/** Cheap structural diff so an unchanged column skips its PATCH. */
export function optionsChanged(cleaned: StatusOption[], original: StatusOption[]): boolean {
  return JSON.stringify(cleaned) !== JSON.stringify(original);
}
